import { useState } from 'react';
import { ArrowRight, Check, Building2, Cpu, DollarSign, Users } from 'lucide-react';
import { ForecasterLogo } from '../components/ForecasterLogo';

export const DesignPartner = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        role: '',
        gpuSpend: '',
        infrastructure: '',
        painPoint: '',
        timeline: ''
    });
    const [submitted, setSubmitted] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Send to Formspree (free email forwarding)
            // Replace YOUR_FORM_ID with your Formspree form ID
            const response = await fetch('https://formspree.io/f/xwpkvjjz', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    ...formData,
                    _subject: `Design Partner Application: ${formData.company}`,
                    _replyto: formData.email
                })
            });

            if (response.ok) {
                setSubmitted(true);
            } else {
                // Fallback: open mailto link
                const mailtoBody = `
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Role: ${formData.role}
GPU Spend: ${formData.gpuSpend}
Infrastructure: ${formData.infrastructure}
Pain Point: ${formData.painPoint}
Timeline: ${formData.timeline}
                `.trim();

                window.location.href = `mailto:forecaster.k8s@gmail.com?subject=Design Partner Application: ${encodeURIComponent(formData.company)}&body=${encodeURIComponent(mailtoBody)}`;
                setSubmitted(true);
            }
        } catch {
            // Fallback to mailto on error
            const mailtoBody = `
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Role: ${formData.role}
GPU Spend: ${formData.gpuSpend}
Infrastructure: ${formData.infrastructure}
Pain Point: ${formData.painPoint}
Timeline: ${formData.timeline}
            `.trim();

            window.location.href = `mailto:forecaster.k8s@gmail.com?subject=Design Partner Application: ${encodeURIComponent(formData.company)}&body=${encodeURIComponent(mailtoBody)}`;
            setSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-obsidian-900 text-white flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <div className="w-16 h-16 rounded-full bg-volt-500/20 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-8 h-8 text-volt-400" />
                    </div>
                    <h1 className="text-3xl font-bold mb-4">Application Received</h1>
                    <p className="text-gray-400 mb-8">
                        Thanks for your interest in becoming a design partner.
                        We'll review your application and reach out within 48 hours.
                    </p>
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-volt-500 text-obsidian-900 rounded-xl font-medium hover:bg-volt-400 transition-colors"
                    >
                        Back to Home
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-obsidian-900 text-white">
            {/* Header */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian-900/80 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                    <a href="/"><ForecasterLogo /></a>
                </div>
            </nav>

            <div className="pt-32 pb-20 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Hero */}
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-volt-500/10 border border-volt-500/30 text-sm text-volt-400 mb-6">
                            Limited to 10 companies
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                            Design Partner Program
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Shape the future of AI infrastructure. Get early access, direct founder support,
                            and lock in pricing before we launch.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Left: Benefits */}
                        <div>
                            <h2 className="text-2xl font-bold mb-8">What You Get</h2>

                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-volt-500/10 flex items-center justify-center flex-shrink-0">
                                        <DollarSign className="w-6 h-6 text-volt-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-1">Free 90-Day Pilot</h3>
                                        <p className="text-gray-400">
                                            Run Forecaster in your environment at no cost. See real savings before committing.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-volt-500/10 flex items-center justify-center flex-shrink-0">
                                        <Users className="w-6 h-6 text-volt-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-1">Direct Founder Access</h3>
                                        <p className="text-gray-400">
                                            Private Slack channel with our founding team. Your feedback shapes the product.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-volt-500/10 flex items-center justify-center flex-shrink-0">
                                        <Cpu className="w-6 h-6 text-volt-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-1">Priority Feature Requests</h3>
                                        <p className="text-gray-400">
                                            Your use cases get built first. Direct input on our roadmap.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-volt-500/10 flex items-center justify-center flex-shrink-0">
                                        <Building2 className="w-6 h-6 text-volt-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-1">Locked Pricing (50% Off)</h3>
                                        <p className="text-gray-400">
                                            Design partners lock in 50% off our launch pricing. Forever.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* What We're Looking For */}
                            <div className="mt-12 p-6 rounded-2xl bg-white/[0.02] border border-white/10">
                                <h3 className="text-lg font-semibold mb-4">Ideal Partners</h3>
                                <ul className="space-y-3 text-gray-400">
                                    <li className="flex items-start gap-2">
                                        <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                                        <span>Running LLMs in production on Kubernetes</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                                        <span>Monthly GPU spend $50K+ (or growing fast)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                                        <span>Can deploy to staging within 2 weeks</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                                        <span>Willing to share feedback and use cases</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Right: Application Form */}
                        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8">
                            <h2 className="text-2xl font-bold mb-6">Apply Now</h2>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            Your Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-volt-500 transition-colors"
                                            placeholder="Jane Smith"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            Work Email *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-volt-500 transition-colors"
                                            placeholder="jane@company.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            Company *
                                        </label>
                                        <input
                                            type="text"
                                            name="company"
                                            required
                                            value={formData.company}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-volt-500 transition-colors"
                                            placeholder="Acme AI"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            Your Role *
                                        </label>
                                        <input
                                            type="text"
                                            name="role"
                                            required
                                            value={formData.role}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-volt-500 transition-colors"
                                            placeholder="Head of Platform"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        Monthly GPU Spend *
                                    </label>
                                    <select
                                        name="gpuSpend"
                                        required
                                        value={formData.gpuSpend}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-volt-500 transition-colors"
                                    >
                                        <option value="" className="bg-obsidian-800">Select range</option>
                                        <option value="under-10k" className="bg-obsidian-800">Under $10K</option>
                                        <option value="10k-50k" className="bg-obsidian-800">$10K - $50K</option>
                                        <option value="50k-100k" className="bg-obsidian-800">$50K - $100K</option>
                                        <option value="100k-500k" className="bg-obsidian-800">$100K - $500K</option>
                                        <option value="500k-plus" className="bg-obsidian-800">$500K+</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        Current Infrastructure *
                                    </label>
                                    <select
                                        name="infrastructure"
                                        required
                                        value={formData.infrastructure}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-volt-500 transition-colors"
                                    >
                                        <option value="" className="bg-obsidian-800">Select your setup</option>
                                        <option value="k8s-vllm" className="bg-obsidian-800">Kubernetes + vLLM</option>
                                        <option value="k8s-sglang" className="bg-obsidian-800">Kubernetes + SGLang</option>
                                        <option value="k8s-other" className="bg-obsidian-800">Kubernetes + Other</option>
                                        <option value="serverless" className="bg-obsidian-800">Serverless (RunPod, Modal, etc.)</option>
                                        <option value="cloud-ml" className="bg-obsidian-800">Cloud ML (SageMaker, Vertex, etc.)</option>
                                        <option value="other" className="bg-obsidian-800">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        What's your biggest infrastructure pain point? *
                                    </label>
                                    <textarea
                                        name="painPoint"
                                        required
                                        value={formData.painPoint}
                                        onChange={handleChange}
                                        rows={3}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-volt-500 transition-colors resize-none"
                                        placeholder="e.g., GPU costs growing 20% monthly, cold starts killing user experience, team spending too much time on infra..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        When could you start a pilot?
                                    </label>
                                    <select
                                        name="timeline"
                                        value={formData.timeline}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-volt-500 transition-colors"
                                    >
                                        <option value="" className="bg-obsidian-800">Select timeline</option>
                                        <option value="immediately" className="bg-obsidian-800">Immediately</option>
                                        <option value="2-weeks" className="bg-obsidian-800">Within 2 weeks</option>
                                        <option value="1-month" className="bg-obsidian-800">Within 1 month</option>
                                        <option value="quarter" className="bg-obsidian-800">This quarter</option>
                                        <option value="evaluating" className="bg-obsidian-800">Just evaluating</option>
                                    </select>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-volt-500 text-obsidian-900 rounded-xl font-semibold hover:bg-volt-400 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                                    {!isSubmitting && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                                </button>

                                <p className="text-center text-sm text-gray-500">
                                    We'll respond within 48 hours
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
